import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, MenuItem, Alert, Snackbar, Autocomplete, CircularProgress, Paper,
} from '@mui/material';
import { TaskAlt } from '@mui/icons-material';
import { procurementService } from '../../services/procurementService';
import { formatIndianNumber } from '../../utils/currency';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill, WorkspaceHeader, TotalsStrip, ValidationStrip,
} from '../../components/common/workspaceKit';

const n = (v: string) => Number(v) || 0;
const r2 = (x: number) => Math.round(x * 100) / 100;
const MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH', 'BANK_TRANSFER'];
const SECTIONS = ['', '194C', '194J', '194I', '194H', '194A', '194Q'];

const VendorPaymentNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [f, setF] = useState({
    vendorId: '', invoiceId: '', paymentDate: new Date().toISOString().slice(0, 10), grossAmount: '',
    tdsSection: '', tdsAmount: '', paymentMode: 'NEFT', bankReference: '', notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { procurementService.getVendors().then((v: any) => setVendors(v.vendors || v || [])).catch(() => {}); }, []);
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));

  // When a vendor is chosen, load their approved (payable) bills so the payment
  // can be linked to a specific purchase invoice and the amount pre-filled.
  const onVendor = async (vendorId: string) => {
    set({ vendorId, invoiceId: '' });
    setInvoices([]);
    if (!vendorId) return;
    try {
      const res: any = await procurementService.getPurchaseInvoices({ vendorId, status: 'APPROVED' });
      setInvoices(res.invoices || res || []);
    } catch { setInvoices([]); }
  };
  const onInvoice = (invoiceId: string) => {
    const inv = invoices.find((i) => String(i.id) === String(invoiceId));
    if (inv) set({ invoiceId, grossAmount: String(inv.balance_amount ?? inv.total_amount ?? '') });
    else set({ invoiceId: '' });
  };

  const net = useMemo(() => r2(n(f.grossAmount) - n(f.tdsAmount)), [f.grossAmount, f.tdsAmount]);
  const valid = f.vendorId && n(f.grossAmount) > 0;

  const submit = async () => {
    setError(null);
    if (!valid) { setError('Vendor and a gross amount are required'); return; }
    setSaving(true);
    try {
      await procurementService.createVendorPayment({
        vendorId: f.vendorId, invoiceId: f.invoiceId || undefined, paymentDate: f.paymentDate, grossAmount: n(f.grossAmount),
        tdsSection: f.tdsSection || undefined, tdsAmount: n(f.tdsAmount) || 0, netAmount: net,
        paymentMode: f.paymentMode, bankReference: f.bankReference || undefined, notes: f.notes || undefined,
      });
      setToast('Payment created');
      setTimeout(() => navigate('/procurement/payments'), 800);
    } catch (e: any) { setError(e.message || 'Failed to create payment'); } finally { setSaving(false); }
  };

  const selectedVendor = vendors.find((v) => String(v.id) === String(f.vendorId)) || null;

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
      <WorkspaceHeader
        onBack={() => navigate('/procurement/payments')}
        title="New Vendor Payment"
        subtitle="Pay a supplier with TDS and see the bank & ledger impact."
        chip={<StatusPill label="DRAFT" tone="draft" />}
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/procurement/payments')} disabled={saving} sx={outBtn}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <TaskAlt sx={{ fontSize: 18 }} />}
              onClick={submit}
              disabled={saving || !valid}
              sx={priBtn}
            >
              {saving ? 'Saving…' : 'Create Payment'}
            </Button>
          </>
        }
      />

      <Box sx={{ px: PX, pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Payment details */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Payment Details</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Supplier" required />
              <Autocomplete
                size="small"
                options={vendors}
                value={selectedVendor}
                onChange={(_, v) => onVendor(v ? String(v.id) : '')}
                getOptionLabel={(o) => o.company_name || o.name || ''}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                renderInput={(params) => <TextField {...params} placeholder="Select supplier" sx={fieldSx} />}
              />
            </Box>
            <Box>
              <FieldLabel text="Against Invoice" />
              <TextField
                select fullWidth size="small" value={f.invoiceId} onChange={(e) => onInvoice(e.target.value)} sx={fieldSx}
                disabled={!f.vendorId}
                helperText={f.vendorId && !invoices.length ? 'No approved bills for this supplier' : 'Optional — links payment & pre-fills amount'}
              >
                <MenuItem value="">— On account (no invoice) —</MenuItem>
                {invoices.map((i) => (
                  <MenuItem key={i.id} value={String(i.id)}>
                    {(i.invoice_no || i.invoice_number || `INV-${i.id}`)} · Bal {formatIndianNumber(Number(i.balance_amount ?? i.total_amount ?? 0))}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Payment Date" required />
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={f.paymentDate} onChange={(e) => set({ paymentDate: e.target.value })} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Payment Mode" />
              <TextField select fullWidth size="small" value={f.paymentMode} onChange={(e) => set({ paymentMode: e.target.value })} sx={fieldSx}>
                {MODES.map((m) => <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Bank Reference" />
              <TextField fullWidth size="small" value={f.bankReference} onChange={(e) => set({ bankReference: e.target.value })} sx={fieldSx} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Notes" />
              <TextField fullWidth size="small" value={f.notes} onChange={(e) => set({ notes: e.target.value })} sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* Amounts */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Amounts</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Gross Amount" required />
              <TextField fullWidth size="small" type="number" value={f.grossAmount} onChange={(e) => set({ grossAmount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="TDS Section" />
              <TextField select fullWidth size="small" value={f.tdsSection} onChange={(e) => set({ tdsSection: e.target.value })} sx={fieldSx}>
                {SECTIONS.map((s) => <MenuItem key={s || 'none'} value={s}>{s || 'None'}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="TDS Amount" />
              <TextField fullWidth size="small" type="number" value={f.tdsAmount} onChange={(e) => set({ tdsAmount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* Totals + status strip */}
        <Box sx={{ mb: 1.5 }}>
          <TotalsStrip
            cells={[
              { label: 'Gross Amount', value: formatIndianNumber(n(f.grossAmount)) },
              { label: 'TDS', value: formatIndianNumber(n(f.tdsAmount)) },
              { label: 'Net Paid', value: formatIndianNumber(net), tone: 'primary' },
              { label: 'Mode', value: f.paymentMode },
            ]}
          />
        </Box>

        {/* Validation strip */}
        <ValidationStrip
          items={[
            { ok: !!f.vendorId, label: 'Vendor selected' },
            { ok: !!f.paymentDate, label: 'Payment date set' },
            { ok: n(f.grossAmount) > 0, label: 'Gross amount entered' },
          ]}
        />
      </Box>

      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default VendorPaymentNewPage;
